import { Client } from '@hero/schema';
import { UserIcon } from '@hero/web-icons';
import { cn } from '@hero/web-lib';

export interface ClientDisplayProps {
  /** The display name of the {@link Client}. */
  clientName: string;
  className?: string;
  /** When set, the pill becomes a button (e.g. open change-client modal). */
  onClick?: () => void;
}

/**
 * Inline pill that shows a `Client` name (CRM relations-v2 entity).
 * Renamed from `CustomerDisplay`.
 */
export function ClientDisplay(props: ClientDisplayProps) {
  const className = cn(
    'inline-flex items-center gap-1.5 px-2 py-1 bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 font-medium text-xs rounded-md border border-blue-200 shadow-sm min-w-0 max-w-full overflow-hidden',
    props.onClick && 'cursor-pointer hover:border-blue-300 hover:from-blue-100 hover:to-indigo-100',
    props.className,
  );

  const content = (
    <>
      <UserIcon className="h-3.5 w-3.5 text-blue-600 flex-shrink-0" strokeWidth={2} />
      <span className="text-xs text-blue-700 font-medium truncate">{props.clientName}</span>
    </>
  );

  if (props.onClick) {
    return (
      <button
        type="button"
        className={className}
        onClick={e => {
          e.stopPropagation();
          props.onClick?.();
        }}>
        {content}
      </button>
    );
  }

  return <div className={className}>{content}</div>;
}

export interface CustomerDisplayProps {
  /** Display name of the underlying `Client`. Kept as `customerName` for
   *  back-compat with the pre-relations-v2 vocabulary. */
  customerName: string;
  className?: string;
}

/**
 * @deprecated Use {@link ClientDisplay} with `clientName` instead.
 *
 * Compatibility alias kept so `apps/customer-dashboard` (the currently-live
 * unified dashboard at `app.tryhero.io`) keeps compiling while the last few
 * call sites still say `<CustomerDisplay customerName={...} />`. Remove once
 * every consumer has migrated to `<ClientDisplay clientName={...} />`.
 */
export function CustomerDisplay({ customerName, className }: CustomerDisplayProps) {
  return <ClientDisplay clientName={customerName} className={className} />;
}
